import { useNavigate } from 'react-router-dom';
import HelpOutline from '@mui/icons-material/HelpOutline';
import Home from '@mui/icons-material/Home';
import Logout from '@mui/icons-material/Logout';
import PersonOutline from '@mui/icons-material/PersonOutline';
import {
  AppBar,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import NavLogo from '~/assets/NavLogo.svg';
import { useCurrentUser } from '~/shared/hooks/useCurrentUser';
import { UserRole } from '~/shared/types';
import { AvatarImage } from '~/shared/components/AvatarImage';
import { clearPostLoginNavigateState } from '~/shared/utils/authReturnTo';
import { FOOTER_CONTACT_URL } from '~/shared/constants';
export const Header = () => {
  const { user: currentUser } = useCurrentUser();
  const { logout } = useAuth0();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    clearPostLoginNavigateState();
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  return (
    <StyledAppBar position="static" elevation={0}>
      <StyledToolbar>
        <Stack direction="row" alignItems="center" spacing={2}>
          <LogoButton
            type="button"
            aria-label={currentUser ? 'Go to matters' : 'Synapsis'}
            disabled={!currentUser}
            onClick={() => navigate('/matters')}
          >
            <img src={NavLogo} alt="" width={123} height={39} />
          </LogoButton>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1}>
          {currentUser ? (
            <>
              <IconButton
                aria-controls="user-menu"
                aria-haspopup="true"
                aria-expanded={Boolean(anchorEl)}
                aria-label="Open user menu"
                onClick={handleUserMenuOpen}
                color="inherit"
              >
                <StyledAvatar
                  avatarUrl={currentUser.avatarUrl}
                  initialsLabel={currentUser.name ?? currentUser.email ?? '—'}
                />
              </IconButton>
              <Menu
                id="user-menu"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleUserMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem
                  onClick={() => {
                    handleUserMenuClose();
                    navigate('/matters');
                  }}
                >
                  <StyledMenuItemIcon>
                    <Home fontSize="small" />
                  </StyledMenuItemIcon>
                  <ListItemText>Matters</ListItemText>
                </MenuItem>
                {currentUser?.role === UserRole.EXPERT ? (
                  <MenuItem
                    onClick={() => {
                      handleUserMenuClose();
                      navigate(`/experts/${currentUser?.id}`);
                    }}
                  >
                    <StyledMenuItemIcon>
                      <PersonOutline fontSize="small" />
                    </StyledMenuItemIcon>
                    <ListItemText>Profile</ListItemText>
                  </MenuItem>
                ) : null}
                {/* TODO: Restore an Account menu item (e.g. Stripe Connect dashboard or legacy account URL) once Stripe account / payout flow is finalized. */}
                <MenuItem
                  component="a"
                  href={FOOTER_CONTACT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleUserMenuClose}
                >
                  <StyledMenuItemIcon>
                    <HelpOutline fontSize="small" />
                  </StyledMenuItemIcon>
                  <ListItemText>Contact support</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <StyledMenuItemIcon>
                    <Logout fontSize="small" />
                  </StyledMenuItemIcon>
                  <ListItemText>Logout</ListItemText>
                </MenuItem>
              </Menu>
            </>
          ) : null}
        </Stack>
      </StyledToolbar>
    </StyledAppBar>
  );
};

const StyledMenuItemIcon = styled(ListItemIcon)({
  minWidth: 40,
});

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.darkNavy,
  color: theme.palette.common.white,
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  justifyContent: 'space-between',
  gap: theme.spacing(2),
}));

const StyledAvatar = styled(AvatarImage)(({ theme }) => ({
  width: 32,
  height: 32,
  backgroundColor: theme.palette.teal.main,
  color: theme.palette.darkNavy,
  fontSize: theme.typography.caption.fontSize,
}));

const LogoButton = styled('button')(({ theme }) => ({
  cursor: 'pointer',
  border: 'none',
  padding: 0,
  margin: 0,
  background: 'none',
  display: 'flex',
  alignItems: 'center',
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.common.white}`,
    outlineOffset: 2,
  },
  '&:disabled': {
    cursor: 'default',
  },
}));
